// Realtime client — owns the single WebSocket connection to the
// API gateway (Req 24.x). Tracks `last_event_id` so a reconnect
// can ask the server to replay missed events; queues outgoing
// frames while disconnected and flushes them on reconnect.
//
// Architecture:
//
//   - `RealtimeProvider` holds a `RealtimeClient` instance and
//     exposes it via context. The instance is created lazily on
//     first connect so SSR / unit tests don't open a socket.
//   - The connection only opens when there is a session token in
//     `sessionStorage` (so the placeholder demo doesn't try to
//     hit the API).
//   - Reconnect strategy: full jitter exponential backoff capped
//     at 30 seconds, retry forever. WS gateway re-verifies the
//     session token on every upgrade (Req 2.7) — if the server
//     closes 4401 we surface a `kicked` event so the UI can
//     prompt re-login.
//   - All frames go through the shared `parseWsFrame` runtime
//     guard from `@link/shared` so we never dispatch on a frame
//     the server schema rejects.

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  parseWsFrame,
  type WsEventFrame,
  type WsFrame,
  type WsHelloFrame,
  type WsSubscribeFrame,
  type WsUnsubscribeFrame,
} from "@link/shared";

import {
  openSessionSocket,
  readSessionToken,
  terminateSuspendedSession,
} from "../lib/http";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ConnectionState =
  | "disconnected"
  | "connecting"
  | "ready"
  | "resync_required";

type EventHandler = (frame: WsEventFrame) => void;

interface RealtimeClient {
  state: ConnectionState;
  /** Last event id seen — survives reconnects. */
  lastEventId: number;
  subscribe: (topics: ReadonlyArray<string>, handler: EventHandler) => () => void;
  /** Force a reconnect (used by `kicked` / token-rotation flows). */
  reconnect: () => void;
}

interface RealtimeContextValue {
  client: RealtimeClient;
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

interface Subscription {
  topics: Set<string>;
  handler: EventHandler;
}

export class RealtimeManager implements RealtimeClient {
  state: ConnectionState = "disconnected";
  lastEventId = 0;

  private socket: WebSocket | null = null;
  private subscriptions = new Set<Subscription>();
  private topicRefCounts = new Map<string, number>();
  private outbound: WsFrame[] = [];
  private retryAttempt = 0;
  private retryTimer: number | null = null;
  private heartbeatTimer: number | null = null;
  private connectTimeoutTimer: number | null = null;
  private listeners = new Set<() => void>();
  private explicitClose = false;
  private seenEvents = new Set<number>();
  private resyncTimer: number | null = null;
  private lastServerFrameAt = 0;

  resume(): void {
    if (this.state !== "ready" || Date.now() - this.lastServerFrameAt > 60_000) this.reconnect();
    else this.refreshCachedViews();
  }

  /** Coalesce subscription/focus barriers; no background polling. */
  refreshCachedViews(): void {
    if (this.resyncTimer !== null) return;
    this.resyncTimer = window.setTimeout(() => {
      this.resyncTimer = null;
      window.dispatchEvent(new CustomEvent("link:realtime-resync"));
    }, 100);
  }

  /** Subscribe to one or more server topics. Returns an unsubscribe. */
  subscribe(topics: ReadonlyArray<string>, handler: EventHandler): () => void {
    const uniqueTopics = new Set(topics.filter((topic) => topic.length > 0));
    const sub: Subscription = { topics: uniqueTopics, handler };
    this.subscriptions.add(sub);
    const addedTopics: string[] = [];
    for (const topic of uniqueTopics) {
      const count = this.topicRefCounts.get(topic) ?? 0;
      this.topicRefCounts.set(topic, count + 1);
      if (count === 0) addedTopics.push(topic);
    }
    if (this.state === "ready") {
      for (let i = 0; i < addedTopics.length; i += 256) this.send(this.makeSubscribe(addedTopics.slice(i, i + 256)));
    } else if (this.state === "disconnected") {
      this.connect();
    }
    return () => {
      this.subscriptions.delete(sub);
      const removedTopics: string[] = [];
      for (const topic of uniqueTopics) {
        const count = this.topicRefCounts.get(topic) ?? 0;
        if (count <= 1) {
          this.topicRefCounts.delete(topic);
          removedTopics.push(topic);
        } else {
          this.topicRefCounts.set(topic, count - 1);
        }
      }
      if (this.state === "ready") {
        for (let i = 0; i < removedTopics.length; i += 256) this.send(this.makeUnsubscribe(removedTopics.slice(i, i + 256)));
      }
    };
  }

  reconnect(): void {
    this.cleanup();
    this.connect();
  }

  destroy(): void {
    this.explicitClose = true;
    this.cleanup();
    this.setState("disconnected");
    this.subscriptions.clear();
    this.topicRefCounts.clear();
    this.listeners.clear();
    this.outbound = [];
    this.seenEvents.clear();
    this.lastEventId = 0;
  }

  /** React-friendly: call from `useEffect` to subscribe to state changes. */
  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private setState(next: ConnectionState): void {
    if (this.state === next) return;
    this.state = next;
    for (const listener of this.listeners) listener();
  }

  private connect(): void {
    if (typeof window === "undefined") return;
    if (this.socket !== null) return;
    const token = readSessionToken();
    if (token === null || token.length === 0) {
      // Nothing to authenticate with; stay disconnected. Auth flow
      // will call `reconnect()` once the token lands.
      return;
    }

    this.explicitClose = false;
    this.setState("connecting");

    let socket: WebSocket;
    try {
      socket = openSessionSocket();
    } catch {
      this.setState("disconnected");
      this.scheduleRetry();
      return;
    }
    this.socket = socket;

    // Some browsers only surface an `error` when an HTTP proxy rejects a
    // WebSocket upgrade and never deliver the close event we normally use to
    // schedule a retry. Do not let that leave the client stuck in
    // `connecting` forever.
    this.connectTimeoutTimer = window.setTimeout(() => {
      if (this.socket !== socket || this.state === "ready") return;
      try {
        socket.close();
      } catch {
        // The socket may still be in CONNECTING. Clearing our reference and
        // retrying is enough; the browser will discard it once it settles.
      }
      if (this.socket === socket) this.socket = null;
      this.setState("disconnected");
      this.scheduleRetry();
    }, 10_000);

    socket.onopen = () => {
      if (this.socket !== socket) return;
      // Send hello as soon as the socket opens so the server can
      // replay missed events under our `last_event_id`.
      const hello: WsHelloFrame = {
        t: "hello",
        client_time_ms: Date.now(),
        last_event_id: this.lastEventId,
      };
      socket.send(JSON.stringify(hello));
    };

    socket.onmessage = (ev) => { if (this.socket === socket) this.onMessage(ev); };

    socket.onerror = () => {
      // The browser surfaces close/error on its own; nothing to do.
    };

    socket.onclose = (ev) => {
      if (this.socket !== socket) return;
      this.clearConnectTimeout();
      if (this.heartbeatTimer !== null) {
        window.clearInterval(this.heartbeatTimer);
        this.heartbeatTimer = null;
      }
      this.socket = null;
      this.setState("disconnected");
      if (this.explicitClose) return;
      // 4401 = invalid/expired session token. Don't retry — the UI
      // needs to gate re-login.
      if (ev.code === 4401) return;
      // 4403 is emitted after a moderation suspension. Clear the local
      // session and show the specific explanation on the sign-in screen.
      if (ev.code === 4403) {
        terminateSuspendedSession();
        return;
      }
      this.scheduleRetry();
    };
  }

  private onMessage(ev: MessageEvent): void {
    let payload: unknown;
    try {
      payload = JSON.parse(typeof ev.data === "string" ? ev.data : "");
    } catch {
      return;
    }
    const parsed = parseWsFrame(payload);
    if (!parsed.ok) return;
    const frame = parsed.frame;
    this.lastServerFrameAt = Date.now();

    switch (frame.t) {
      case "ready":
        // Server accepted the hello; we can flush any queued
        // subscribe frames and outbound writes now.
        this.retryAttempt = 0;
        this.clearConnectTimeout();
        this.setState("ready");
        this.startHeartbeat();
        this.replaySubscriptions();
        this.flushOutbound();
        this.refreshCachedViews();
        return;
      case "subscribed":
        this.refreshCachedViews();
        return;
      case "event":
        // Presence uses ephemeral timestamp IDs, not durable outbox IDs.
        // Mixing those namespaces would advance the replay cursor years ahead
        // of every stored event (and drop simultaneous presence changes).
        if (frame.type !== "presence.online" && frame.type !== "presence.offline") {
          this.lastEventId = Math.max(this.lastEventId, frame.event_id);
          if (this.seenEvents.has(frame.event_id)) return;
          this.seenEvents.add(frame.event_id);
          if (this.seenEvents.size > 4096) this.seenEvents.delete(this.seenEvents.values().next().value!);
        }
        for (const sub of this.subscriptions) {
          if (sub.topics.has(frame.topic)) sub.handler(frame);
        }
        window.dispatchEvent(
          new CustomEvent("link:realtime-event", { detail: frame }),
        );
        if (frame.type === "session.kicked") {
          this.explicitClose = true;
          try {
            this.socket?.close(1000, "session ended");
          } catch {
            // The server may already have closed the connection with 4403.
          }
          terminateSuspendedSession();
        }
        return;
      case "resync_required":
        // The outbox window aged out our `last_event_id`; the UI
        // needs to refetch any cached views from REST. We surface
        // the state so a banner can fire.
        this.lastEventId = 0;
        this.seenEvents.clear();
        // The subscription/socket is still valid; do not stop heartbeats or
        // strand new subscriptions while REST catches up.
        this.setState("ready");
        this.refreshCachedViews();
        return;
      case "error":
        // Server-emitted protocol error. Soft-log only; the higher
        // level UI surfaces it via the API call that triggered it.
        return;
      default:
        // ack / hello / subscribe / unsubscribe are echo-shaped or
        // client-originating; ignore on the inbound side.
        return;
    }
  }

  private replaySubscriptions(): void {
    const topics = Array.from(this.topicRefCounts.keys());
    if (topics.length === 0) return;
    for (let i = 0; i < topics.length; i += 256) this.send(this.makeSubscribe(topics.slice(i, i + 256)));
  }

  private makeSubscribe(topics: ReadonlyArray<string>): WsSubscribeFrame {
    return {
      t: "subscribe",
      topics: [...topics],
    };
  }

  private makeUnsubscribe(topics: ReadonlyArray<string>): WsUnsubscribeFrame {
    return {
      t: "unsubscribe",
      topics: [...topics],
    };
  }

  private send(frame: WsFrame): void {
    if (this.state === "ready" && this.socket !== null) {
      this.socket.send(JSON.stringify(frame));
    } else {
      this.outbound.push(frame);
    }
  }

  private flushOutbound(): void {
    if (this.socket === null) return;
    const queued = this.outbound;
    this.outbound = [];
    for (const frame of queued) this.socket.send(JSON.stringify(frame));
  }

  private scheduleRetry(): void {
    if (this.retryTimer !== null) return;
    this.retryAttempt += 1;
    // Full-jitter backoff: pick a random delay in [0, base * 2^n)
    // capped at 30s so a flapping server doesn't burn the client.
    const cap = 30_000;
    const base = 500;
    const max = Math.min(cap, base * 2 ** Math.min(this.retryAttempt, 6));
    const delay = Math.floor(Math.random() * max);
    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = null;
      this.connect();
    }, delay);
  }

  private startHeartbeat(): void {
    if (this.heartbeatTimer !== null) window.clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = window.setInterval(() => {
      if (this.state !== "ready" || this.socket === null) return;
      if (Date.now() - this.lastServerFrameAt > 60_000) { this.reconnect(); return; }
      this.socket.send(
        JSON.stringify({ t: "ack", event_id: this.lastEventId }),
      );
    }, 25_000);
  }

  private clearConnectTimeout(): void {
    if (this.connectTimeoutTimer === null) return;
    window.clearTimeout(this.connectTimeoutTimer);
    this.connectTimeoutTimer = null;
  }

  private cleanup(): void {
    if (this.resyncTimer !== null) window.clearTimeout(this.resyncTimer);
    this.resyncTimer = null;
    this.clearConnectTimeout();
    if (this.heartbeatTimer !== null) {
      window.clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.retryTimer !== null) {
      window.clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    if (this.socket !== null) {
      this.explicitClose = true;
      const socket = this.socket;
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      try {
        socket.close(1000, "client reconnect");
      } catch {
        // ignore
      }
      this.socket = null;
    }
  }
}

// ---------------------------------------------------------------------------
// Provider + hook
// ---------------------------------------------------------------------------

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const managerRef = useRef<RealtimeManager | null>(null);
  if (managerRef.current === null) {
    managerRef.current = new RealtimeManager();
  }

  const value = useMemo<RealtimeContextValue>(
    () => ({ client: managerRef.current! }),
    [],
  );

  // Tear down on unmount so HMR / route changes don't leave dangling
  // sockets behind.
  useEffect(() => {
    const manager = managerRef.current!;
    const reconnectIfNeeded = (): void => {
      manager.resume();
    };
    const reconnectWhenVisible = (): void => {
      if (document.visibilityState === "visible") reconnectIfNeeded();
    };
    window.addEventListener("online", reconnectIfNeeded);
    window.addEventListener("focus", reconnectIfNeeded);
    document.addEventListener("visibilitychange", reconnectWhenVisible);
    return () => {
      window.removeEventListener("online", reconnectIfNeeded);
      window.removeEventListener("focus", reconnectIfNeeded);
      document.removeEventListener("visibilitychange", reconnectWhenVisible);
      manager.destroy();
    };
  }, []);

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
}

export function useRealtime(): RealtimeClient {
  const ctx = useContext(RealtimeContext);
  if (ctx === null) {
    throw new Error("useRealtime must be used inside <RealtimeProvider>");
  }
  return ctx.client;
}

/**
 * React-friendly subscription hook. Subscribes to `topics` for the
 * lifetime of the component and re-runs `handler` on every event
 * matching one of those topics.
 */
export function useRealtimeSubscription(
  topics: ReadonlyArray<string>,
  handler: EventHandler,
): ConnectionState {
  const client = useRealtime();
  const [state, setState] = useState<ConnectionState>(client.state);
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    // Track state changes so callers can show a "reconnecting…"
    // banner.
    const manager = client as unknown as RealtimeManager;
    if (typeof manager.onChange === "function") {
      const unbind = manager.onChange(() => setState(manager.state));
      return unbind;
    }
    return () => undefined;
  }, [client]);

  useEffect(() => {
    if (topics.length === 0) return undefined;
    return client.subscribe(topics, (frame) => handlerRef.current(frame));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, topics.join("|")]);

  return state;
}
